import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { LxpLayout } from "@/components/lexpractiq/LxpLayout";
import { Award, ArrowRight, User, Briefcase, GraduationCap, Sparkles, Eye, EyeOff, Download, ExternalLink, Star } from "lucide-react";
import { listMyRuns, setPortfolioVisibility } from "@/lib/api/lxp-runs.functions";
import { getMyLxpRoles, setMyLxpRole } from "@/lib/api/lxp-roles.functions";
import { getMyPortfolioSettings, saveMyPortfolioSettings } from "@/lib/api/lxp-portfolio.functions";
import { getMyVerifiedWork } from "@/lib/api/lxp-verified-work.functions";
import { listPublishedSimulations } from "@/lib/api/lxp-simulations.functions";
import { generateLxpCertificatePdf } from "@/lib/api/lxp-certificate-pdf.functions";
import { downloadBase64File } from "@/lib/download-base64";
import {
  LXP_CATEGORIES,
  LXP_CATEGORY_LABELS,
  CATEGORY_TO_ROLE,
  getMyLxpCategory,
  setMyLxpCategory,
} from "@/lib/api/lxp-category.functions";

export const Route = createFileRoute("/_authenticated/lexpractiq/dashboard")({
  head: () => ({ meta: [{ title: "My Dashboard | SA LexPractiq — Legal Experience Platform" }] }),
  component: DashboardPage,
});

type Tab = "overview" | "portfolio" | "recommendations" | "settings";

const ROLE_LABELS: Record<string, string> = {
  lxp_student: "Student / Graduate",
  lxp_recruiter: "Recruiter / Employer",
  lxp_university: "University / Lecturer",
};

function DashboardPage() {
  const runsFn = useServerFn(listMyRuns);
  const rolesFn = useServerFn(getMyLxpRoles);
  const setRoleFn = useServerFn(setMyLxpRole);
  const getSettingsFn = useServerFn(getMyPortfolioSettings);
  const saveSettingsFn = useServerFn(saveMyPortfolioSettings);
  const workFn = useServerFn(getMyVerifiedWork);
  const visFn = useServerFn(setPortfolioVisibility);
  const simsFn = useServerFn(listPublishedSimulations);
  const certPdfFn = useServerFn(generateLxpCertificatePdf);
  const catGetFn = useServerFn(getMyLxpCategory);
  const catSetFn = useServerFn(setMyLxpCategory);

  async function downloadCertPdf(id: string) {
    try {
      const r: any = await certPdfFn({ data: { certificateId: id } });
      downloadBase64File(r.base64, r.filename);
    } catch (e: any) { toast.error(e?.message || "Failed to generate PDF"); }
  }

  const [runs, setRuns] = useState<any[]>([]);
  const [roles, setRoles] = useState<any>({ isStudent: false, isRecruiter: false, isUniversity: false });
  const [settings, setSettings] = useState<any>({ display_name: "", headline: "", bio: "", public: false });
  const [work, setWork] = useState<any>(null);
  const [sims, setSims] = useState<any[]>([]);
  const [tab, setTab] = useState<Tab>("overview");
  const [category, setCategory] = useState<(typeof LXP_CATEGORIES)[number] | null>(null);

  // Onboarding wizard state
  const [showOnboard, setShowOnboard] = useState(false);
  const [onboardStep, setOnboardStep] = useState<"category" | "profile" | "recommend">("category");
  const [interests, setInterests] = useState<string[]>([]);

  async function reload() {
    const [r, ro, se, w, s, c] = await Promise.all([
      runsFn(),
      rolesFn(),
      getSettingsFn(),
      workFn(),
      simsFn({ data: { limit: 100 } }),
      catGetFn(),
    ]);
    setRuns(r.runs);
    setRoles(ro);
    if (se.settings) setSettings(se.settings);
    setWork(w);
    setSims(s.items);
    setCategory(c.category);
    if (!c.onboardedAt) {
      setShowOnboard(true);
      setOnboardStep("category");
    }
  }

  useEffect(() => { reload(); }, []);

  async function pickCategory(cat: (typeof LXP_CATEGORIES)[number]) {
    setCategory(cat);
    try {
      await catSetFn({ data: { category: cat } });
      // Refresh roles so recruiter/university dashboards unlock immediately
      const ro = await rolesFn();
      setRoles(ro);
      setOnboardStep(CATEGORY_TO_ROLE[cat] === "lxp_student" ? "profile" : "recommend");
    } catch (e: any) {
      toast.error(e?.message || "Could not save your role");
    }
  }

  async function saveOnboardProfile(name: string, headline: string) {
    await saveSettingsFn({ data: { display_name: name || "New learner", headline: headline || "", bio: "", public: false } });
    setOnboardStep("recommend");
  }

  async function saveSettings() {
    if (!settings.display_name?.trim()) return toast.error("Display name required");
    await saveSettingsFn({ data: settings });
    toast.success("Portfolio settings saved");
    await reload();
  }

  async function toggleVisibility(subId: string, visible: boolean) {
    await visFn({ data: { submissionId: subId, visible } });
    await reload();
    toast.success(visible ? "Now visible to recruiters" : "Hidden from public portfolio");
  }

  function exportPortfolio() {
    if (!work) return;
    const payload = {
      generated_at: new Date().toISOString(),
      platform: "SA LexPractiq — Legal Experience Platform",
      profile: settings,
      competencies: work.competencies,
      certificates: work.certificates,
      submissions: work.submissions.filter((s: any) => s.portfolio_visible),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sa-lexpractiq-portfolio-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function printPortfolio() { window.print(); }

  const totalXp = runs.reduce((s, r) => s + (r.xp_earned || 0), 0);
  const level = Math.max(1, Math.floor(totalXp / 500) + 1);

  // Recommendations: based on interests + role
  const recommendations = useMemo(() => {
    const notStarted = sims.filter((s) => !runs.some((r) => r.simulation?.slug === s.slug));
    if (roles.isRecruiter || roles.isUniversity) {
      return notStarted.slice(0, 3);
    }
    if (interests.length === 0) {
      return notStarted.filter((s) => s.difficulty === "beginner").slice(0, 3).concat(notStarted.slice(0, 3)).slice(0, 3);
    }
    return notStarted
      .map((s) => ({ s, score: interests.reduce((a, i) => a + (s.practice_area.toLowerCase().includes(i.toLowerCase()) || s.title.toLowerCase().includes(i.toLowerCase()) ? 1 : 0), 0) }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 4)
      .map((x) => x.s);
  }, [sims, runs, interests, roles]);

  const activeRoles = [
    roles.isStudent && "lxp_student",
    roles.isRecruiter && "lxp_recruiter",
    roles.isUniversity && "lxp_university",
  ].filter(Boolean) as string[];

  return (
    <LxpLayout>
      <div className="lxp-container" style={{ padding: "2rem 1.25rem" }}>
        {/* Onboarding Wizard */}
        {showOnboard && (
          <div className="lxp-card" style={{ marginBottom: "1.5rem", background: "linear-gradient(135deg, var(--lxp-accent-glow), transparent)", borderColor: "var(--lxp-accent)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "0.5rem" }}>
              <div>
                <span className="lxp-badge"><Sparkles size={12} /> Welcome to SA LexPractiq</span>
                <h2 style={{ margin: "0.5rem 0 0.25rem" }}>Let's set up your Legal Experience Platform</h2>
                <p className="lxp-muted" style={{ margin: 0, fontSize: "0.9rem" }}>Step {onboardStep === "category" ? "1" : onboardStep === "profile" ? "2" : "3"} of 3</p>
              </div>
              <button className="lxp-btn-ghost" onClick={() => setShowOnboard(false)}>Skip</button>
            </div>

            {onboardStep === "category" && (
              <div style={{ marginTop: "1.25rem" }}>
                <p style={{ margin: "0 0 0.75rem" }}>Which best describes you? Your workspace, AI recommendations and mentor matches adapt to your role.</p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0.5rem" }}>
                  {LXP_CATEGORIES.map((c) => {
                    const active = category === c;
                    return (
                      <button
                        key={c}
                        className={active ? "lxp-btn" : "lxp-btn-ghost"}
                        style={{ padding: "0.75rem", justifyContent: "flex-start", textAlign: "left" }}
                        onClick={() => pickCategory(c)}
                      >
                        {LXP_CATEGORY_LABELS[c]}
                      </button>
                    );
                  })}
                </div>
                <p className="lxp-muted" style={{ fontSize: "0.75rem", marginTop: "0.75rem" }}>
                  You can change this any time from your profile.
                </p>
              </div>
            )}

            {onboardStep === "profile" && (
              <ProfileStep onDone={saveOnboardProfile} initial={settings} />
            )}

            {onboardStep === "recommend" && (
              <div style={{ marginTop: "1.25rem" }}>
                <p style={{ margin: "0 0 0.75rem" }}>Which areas interest you most? (pick any)</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "1rem" }}>
                  {["Labour Law", "Commercial", "Constitutional", "Compliance", "Litigation", "Family", "Criminal"].map((tag) => (
                    <button
                      key={tag}
                      onClick={() => setInterests((prev) => prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag])}
                      className={interests.includes(tag) ? "lxp-btn" : "lxp-btn-ghost"}
                      style={{ padding: "0.35rem 0.7rem", fontSize: "0.8rem" }}
                    >{tag}</button>
                  ))}
                </div>
                <button className="lxp-btn" onClick={() => { setShowOnboard(false); setTab("recommendations"); reload(); }}>
                  Show my recommendations <ArrowRight size={14} />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Header stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1rem", marginBottom: "1.5rem" }}>
          <div className="lxp-card">
            <div className="lxp-muted" style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.08em" }}>Total XP</div>
            <div style={{ fontSize: "1.8rem", fontWeight: 700, color: "var(--lxp-accent)" }}>{totalXp}</div>
            <div className="lxp-muted" style={{ fontSize: "0.85rem" }}>Level {level}</div>
          </div>
          <div className="lxp-card">
            <div className="lxp-muted" style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.08em" }}>Simulations</div>
            <div style={{ fontSize: "1.8rem", fontWeight: 700 }}>{runs.length}</div>
            <div className="lxp-muted" style={{ fontSize: "0.85rem" }}>{runs.filter((r) => r.status === "completed").length} completed</div>
          </div>
          <div className="lxp-card">
            <div className="lxp-muted" style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.08em" }}>Certificates</div>
            <div style={{ fontSize: "1.8rem", fontWeight: 700 }}>{work?.certificates?.length || 0}</div>
            <div className="lxp-muted" style={{ fontSize: "0.85rem" }}>verified</div>
          </div>
          <div className="lxp-card">
            <div className="lxp-muted" style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.08em" }}>Roles</div>
            <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", marginTop: "0.5rem" }}>
              {activeRoles.length === 0 && <span className="lxp-muted" style={{ fontSize: "0.85rem" }}>None yet</span>}
              {activeRoles.map((r) => <span key={r} className="lxp-badge">{ROLE_LABELS[r]?.split(" / ")[0]}</span>)}
            </div>
          </div>
        </div>

        {/* Tab bar */}
        <div style={{ display: "flex", gap: "0.35rem", marginBottom: "1.25rem", borderBottom: "1px solid var(--lxp-border)", overflowX: "auto" }}>
          {([
            ["overview", "Overview"],
            ["recommendations", "Recommendations"],
            ["portfolio", "Verified portfolio"],
            ["settings", "Settings"],
          ] as [Tab, string][]).map(([k, l]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              style={{
                background: "transparent", border: "none", padding: "0.6rem 1rem", cursor: "pointer",
                color: tab === k ? "var(--lxp-accent)" : "var(--lxp-text-muted)", fontWeight: 500,
                borderBottom: tab === k ? "2px solid var(--lxp-accent)" : "2px solid transparent",
                fontSize: "0.9rem", whiteSpace: "nowrap",
              }}
            >{l}</button>
          ))}
        </div>

        {/* Role dashboards shortcuts */}
        <div style={{ display: "grid", gap: "0.75rem", marginBottom: "1.5rem", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
          {roles.isRecruiter && (
            <Link to="/lexpractiq/recruiter" className="lxp-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", textDecoration: "none" }}>
              <div><Briefcase size={18} style={{ verticalAlign: "-3px", marginRight: 6, color: "var(--lxp-accent)" }} /><strong>Recruiter dashboard</strong></div>
              <ArrowRight size={16} />
            </Link>
          )}
          {roles.isRecruiter && (
            <Link to="/lexpractiq/recruiter/assessments" className="lxp-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", textDecoration: "none" }}>
              <div><Briefcase size={18} style={{ verticalAlign: "-3px", marginRight: 6, color: "var(--lxp-accent)" }} /><strong>Custom assessments</strong></div>
              <ArrowRight size={16} />
            </Link>
          )}
          <Link to="/lexpractiq/my-assessments" className="lxp-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", textDecoration: "none" }}>
            <div><Briefcase size={18} style={{ verticalAlign: "-3px", marginRight: 6, color: "var(--lxp-accent)" }} /><strong>My assessments</strong></div>
            <ArrowRight size={16} />
          </Link>
          <Link to="/lexpractiq/portfolio-evidence" className="lxp-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", textDecoration: "none" }}>
            <div><Award size={18} style={{ verticalAlign: "-3px", marginRight: 6, color: "var(--lxp-accent)" }} /><strong>Portfolio evidence</strong></div>
            <ArrowRight size={16} />
          </Link>
          <Link to="/lexpractiq/mentors" className="lxp-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", textDecoration: "none" }}>
            <div><User size={18} style={{ verticalAlign: "-3px", marginRight: 6, color: "var(--lxp-accent)" }} /><strong>Mentor network</strong></div>
            <ArrowRight size={16} />
          </Link>
          <Link to="/lexpractiq/firms" className="lxp-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", textDecoration: "none" }}>
            <div><Briefcase size={18} style={{ verticalAlign: "-3px", marginRight: 6, color: "var(--lxp-accent)" }} /><strong>Law firm portal</strong></div>
            <ArrowRight size={16} />
          </Link>
          {roles.isUniversity && (
            <Link to="/lexpractiq/university" className="lxp-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", textDecoration: "none" }}>
              <div><GraduationCap size={18} style={{ verticalAlign: "-3px", marginRight: 6, color: "var(--lxp-accent)" }} /><strong>University dashboard</strong></div>
              <ArrowRight size={16} />
            </Link>
          )}
        </div>


        {/* TAB: Overview */}
        {tab === "overview" && (
          <>
            <h2 style={{ fontSize: "1.15rem", margin: "1rem 0 0.75rem" }}>My simulations</h2>
            {runs.length === 0 && (
              <div className="lxp-card" style={{ textAlign: "center" }}>
                <p className="lxp-muted">You haven't started any simulations yet.</p>
                <Link to="/lexpractiq/simulations" className="lxp-btn">Browse simulations</Link>
              </div>
            )}
            <div style={{ display: "grid", gap: "0.75rem" }}>
              {runs.map((r) => (
                <Link
                  key={r.id}
                  to="/lexpractiq/simulations/$slug/run"
                  params={{ slug: r.simulation?.slug }}
                  className="lxp-card"
                  style={{ textDecoration: "none", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem" }}
                >
                  <div>
                    <span className="lxp-badge">{r.simulation?.practice_area}</span>
                    <h3 style={{ margin: "0.4rem 0", fontSize: "1rem" }}>{r.simulation?.title}</h3>
                    <div className="lxp-muted" style={{ fontSize: "0.8rem" }}>
                      {r.status === "completed" ? `Completed · ${r.final_score ?? "-"}% · ${r.xp_earned} XP` : `In progress · Task ${r.current_task_index + 1}`}
                    </div>
                  </div>
                  {r.status === "completed" ? <Award size={22} style={{ color: "var(--lxp-accent)" }} /> : <ArrowRight size={16} />}
                </Link>
              ))}
            </div>
          </>
        )}

        {/* TAB: Recommendations */}
        {tab === "recommendations" && (
          <>
            <div className="lxp-card" style={{ marginBottom: "1rem" }}>
              <h3 style={{ margin: "0 0 0.5rem", fontSize: "1rem" }}>
                <Star size={16} style={{ verticalAlign: "-3px", marginRight: 6, color: "var(--lxp-accent)" }} />
                Recommended for you
              </h3>
              <p className="lxp-muted" style={{ margin: "0 0 0.75rem", fontSize: "0.85rem" }}>
                {interests.length > 0 ? `Based on your interests: ${interests.join(", ")}` : "Popular starting points across practice areas."}
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.5rem" }}>
                {["Labour Law", "Commercial", "Constitutional", "Compliance", "Litigation", "Family", "Criminal"].map((tag) => (
                  <button
                    key={tag}
                    onClick={() => setInterests((prev) => prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag])}
                    className={interests.includes(tag) ? "lxp-btn" : "lxp-btn-ghost"}
                    style={{ padding: "0.3rem 0.65rem", fontSize: "0.75rem" }}
                  >{tag}</button>
                ))}
              </div>
            </div>
            <div style={{ display: "grid", gap: "0.75rem", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
              {recommendations.map((s) => (
                <Link key={s.slug} to="/lexpractiq/simulations/$slug" params={{ slug: s.slug }} className="lxp-card" style={{ textDecoration: "none", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  <span className="lxp-badge">{s.practice_area}</span>
                  <h3 style={{ margin: 0, fontSize: "1rem" }}>{s.title}</h3>
                  <p className="lxp-muted" style={{ margin: 0, fontSize: "0.85rem", lineHeight: 1.5, flex: 1 }}>{s.summary}</p>
                  <div style={{ display: "flex", gap: "0.6rem", fontSize: "0.75rem", color: "var(--lxp-text-muted)", borderTop: "1px solid var(--lxp-border)", paddingTop: "0.5rem" }}>
                    <span>⏱ {Math.round(s.duration_min / 60)}h</span>
                    <span>⚡ {s.xp_reward} XP</span>
                    <span style={{ marginLeft: "auto", color: "var(--lxp-accent)" }}>Start →</span>
                  </div>
                </Link>
              ))}
              {recommendations.length === 0 && <p className="lxp-muted">You've explored everything currently available. Check back soon.</p>}
            </div>
            <div className="lxp-card" style={{ marginTop: "1rem" }}>
              <h4 style={{ margin: "0 0 0.5rem", fontSize: "0.95rem" }}>Next steps</h4>
              <ul style={{ margin: 0, paddingLeft: "1.25rem", fontSize: "0.9rem", lineHeight: 1.7 }} className="lxp-muted">
                <li>Complete a Beginner simulation to earn your first certificate.</li>
                <li>Enable your public portfolio in <button onClick={() => setTab("settings")} style={{ background: "none", border: "none", color: "var(--lxp-accent)", cursor: "pointer", padding: 0 }}>Settings</button> so recruiters can find you.</li>
                <li>Mark your best submissions as public in the <button onClick={() => setTab("portfolio")} style={{ background: "none", border: "none", color: "var(--lxp-accent)", cursor: "pointer", padding: 0 }}>Portfolio tab</button>.</li>
              </ul>
            </div>
          </>
        )}

        {/* TAB: Portfolio */}
        {tab === "portfolio" && work && (
          <>
            <div className="lxp-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.75rem", marginBottom: "1rem" }}>
              <div>
                <strong>Public visibility:</strong>{" "}
                <span style={{ color: settings.public ? "var(--lxp-accent)" : "var(--lxp-warn)" }}>
                  {settings.public ? "ON — recruiters can view" : "OFF — private"}
                </span>
                <div className="lxp-muted" style={{ fontSize: "0.8rem", marginTop: 4 }}>
                  Choose which submissions appear on your public portfolio using the eye icons below.
                </div>
              </div>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button className="lxp-btn-ghost" onClick={exportPortfolio}><Download size={14} /> Export JSON</button>
                <button className="lxp-btn-ghost" onClick={printPortfolio}><Download size={14} /> Print / PDF</button>
              </div>
            </div>

            <h3 style={{ fontSize: "1rem", margin: "1rem 0 0.5rem" }}>
              <Award size={16} style={{ verticalAlign: "-3px", marginRight: 6, color: "var(--lxp-accent)" }} />
              Verified certificates ({work.certificates.length})
            </h3>
            <div style={{ display: "grid", gap: "0.5rem" }}>
              {work.certificates.map((c: any) => (
                <div key={c.id} className="lxp-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.75rem" }}>
                  <div>
                    <span className="lxp-badge">{c.practice_area}</span>
                    <strong style={{ display: "block", marginTop: 4 }}>{c.simulation_title}</strong>
                    <div className="lxp-muted" style={{ fontSize: "0.75rem" }}>
                      {c.certificate_number} · Issued {new Date(c.issued_at).toLocaleDateString()}{c.final_score ? ` · ${c.final_score}%` : ""}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                    <button onClick={() => downloadCertPdf(c.id)} className="lxp-btn" style={{ fontSize: "0.8rem" }}>
                      <Download size={12} /> PDF
                    </button>
                    <a href={`/lexpractiq/verify/${c.verification_token}`} target="_blank" rel="noreferrer" className="lxp-btn-ghost" style={{ fontSize: "0.8rem" }}>
                      Verify <ExternalLink size={12} />
                    </a>
                  </div>
                </div>
              ))}
              {work.certificates.length === 0 && <p className="lxp-muted">No certificates yet — complete a simulation to earn one.</p>}
            </div>

            <h3 style={{ fontSize: "1rem", margin: "1.5rem 0 0.5rem" }}>
              Assessed submissions ({work.submissions.length})
            </h3>
            <div style={{ display: "grid", gap: "0.5rem" }}>
              {work.submissions.filter((s: any) => s.ai_scores).map((s: any) => (
                <div key={s.id} className="lxp-card">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: "0.75rem" }}>
                    <div style={{ flex: 1 }}>
                      <span className="lxp-badge">{s.practice_area}</span>
                      <strong style={{ display: "block", marginTop: 4 }}>{s.task_title}</strong>
                      <div className="lxp-muted" style={{ fontSize: "0.75rem" }}>{s.simulation_title}</div>
                      <div style={{ fontSize: "0.85rem", marginTop: "0.5rem", color: "var(--lxp-accent)" }}>
                        Score: {s.ai_scores?.overall_percent ?? "-"}%
                      </div>
                    </div>
                    <button
                      className="lxp-btn-ghost"
                      onClick={() => toggleVisibility(s.id, !s.portfolio_visible)}
                      style={{ padding: "0.4rem 0.6rem" }}
                      aria-label={s.portfolio_visible ? "Hide from public portfolio" : "Show on public portfolio"}
                    >
                      {s.portfolio_visible ? <><Eye size={14} /> Public</> : <><EyeOff size={14} /> Private</>}
                    </button>
                  </div>
                </div>
              ))}
              {work.submissions.filter((s: any) => s.ai_scores).length === 0 && <p className="lxp-muted">No assessed submissions yet.</p>}
            </div>
          </>
        )}

        {/* TAB: Settings */}
        {tab === "settings" && (
          <div className="lxp-card">
            <h3 style={{ margin: "0 0 0.75rem", fontSize: "1rem" }}>Portfolio settings</h3>
            <div style={{ display: "grid", gap: "0.75rem" }}>
              <label style={{ fontSize: "0.85rem" }}>
                <div className="lxp-muted" style={{ marginBottom: 4 }}>Display name</div>
                <input className="lxp-input" value={settings.display_name || ""} onChange={(e) => setSettings({ ...settings, display_name: e.target.value })} />
              </label>
              <label style={{ fontSize: "0.85rem" }}>
                <div className="lxp-muted" style={{ marginBottom: 4 }}>Headline</div>
                <input className="lxp-input" placeholder="LLB candidate · Labour & compliance focus" value={settings.headline || ""} onChange={(e) => setSettings({ ...settings, headline: e.target.value })} />
              </label>
              <label style={{ fontSize: "0.85rem" }}>
                <div className="lxp-muted" style={{ marginBottom: 4 }}>Short bio</div>
                <textarea className="lxp-textarea" rows={4} value={settings.bio || ""} onChange={(e) => setSettings({ ...settings, bio: e.target.value })} />
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.9rem" }}>
                <input type="checkbox" checked={!!settings.public} onChange={(e) => setSettings({ ...settings, public: e.target.checked })} />
                Make my portfolio public (recruiters can discover me)
              </label>
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                <button className="lxp-btn" onClick={saveSettings}>Save</button>
                {settings.public && work?.submissions?.length > 0 && (
                  <a href={`/lexpractiq/portfolio/me`} className="lxp-btn-ghost" onClick={(e) => { e.preventDefault(); toast.info("Your public portfolio URL is /lexpractiq/portfolio/<your-user-id> — share it directly."); }}>
                    View public page
                  </a>
                )}
              </div>
            </div>

            <div className="lxp-divider" />

            <h3 style={{ margin: "0 0 0.75rem", fontSize: "1rem" }}>My roles</h3>
            <p className="lxp-muted" style={{ fontSize: "0.85rem", margin: "0 0 0.75rem" }}>You can hold multiple roles on the Legal Experience Platform.</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
              {(["lxp_student", "lxp_recruiter", "lxp_university"] as const).map((r) => {
                const active = (r === "lxp_student" && roles.isStudent) || (r === "lxp_recruiter" && roles.isRecruiter) || (r === "lxp_university" && roles.isUniversity);
                return (
                  <button
                    key={r}
                    className={active ? "lxp-btn" : "lxp-btn-ghost"}
                    onClick={async () => { if (active) return; await setRoleFn({ data: { role: r } }); await reload(); toast.success("Role added"); }}
                  >{active ? "✓ " : "+ "}{ROLE_LABELS[r]}</button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </LxpLayout>
  );
}

function ProfileStep({ onDone, initial }: { onDone: (name: string, headline: string) => void; initial: any }) {
  const [name, setName] = useState(initial?.display_name || "");
  const [headline, setHeadline] = useState(initial?.headline || "");
  return (
    <div style={{ marginTop: "1.25rem", display: "grid", gap: "0.75rem" }}>
      <label style={{ fontSize: "0.85rem" }}>
        <div className="lxp-muted" style={{ marginBottom: 4 }}>Display name</div>
        <input className="lxp-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name as recruiters will see it" />
      </label>
      <label style={{ fontSize: "0.85rem" }}>
        <div className="lxp-muted" style={{ marginBottom: 4 }}>Headline (optional)</div>
        <input className="lxp-input" value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder="e.g. Final-year LLB · Interested in labour law" />
      </label>
      <button className="lxp-btn" onClick={() => onDone(name, headline)} disabled={!name.trim()}>
        Continue <ArrowRight size={14} />
      </button>
    </div>
  );
}
